/*
 * Loox review rendering, in the ucon design system.
 *
 * Loox's own widget ships its own CSS, which fights the design handoff. The
 * Storefront API returns plain JSON instead, so we own the markup end to end.
 * It is keyless: access is granted by CORS against the shop domain, and the
 * publicStoreId is safe to expose.
 *
 * Two elements, one concern:
 *   <loox-reviews>       PDP - bottomline + distribution bars + paginated list
 *   <loox-reviews-strip> homepage - store aggregate + featured review carousel
 *
 * Both host elements stay [hidden] until data resolves, so a store or product
 * with no reviews never flashes an empty shell.
 *
 * Review text is customer-authored, so every string goes in via textContent -
 * never innerHTML.
 */

const LOOX_BASE = 'https://storefront-api.loox.io/storefront/v1/store/';

function looxBase(storeId) {
  return LOOX_BASE + encodeURIComponent(storeId);
}

// The section band (.section--surface) paints its background and 80px padding
// onto the section's first child, which renders whether or not the inner host
// has content. So reveal the whole <section>, not just the element - otherwise
// every product with no reviews shows an empty coloured strip.
function looxReveal(el) {
  const section = el.closest('section');
  (section || el).hidden = false;
}

// Both bands sit 6-8k px down the page, so the API calls stay out of the load
// window: they start once the page has loaded and the main thread is idle, or
// earlier when the reader nears the band, whichever comes first.
//
// Waiting for the reader alone is not enough. The host and its <section> stay
// [hidden] until the data arrives, and the band then grows by 400-1400px. A
// reader who outruns the round trip sees that growth with the band's spot
// already on screen, and everything below it is pushed out of view (a CLS of
// 0.10-0.25 on a fast mobile scroll). Started at idle, the band is filled long
// before anyone gets there. The near-the-band trigger covers a reader who gets
// there before the page has even finished loading.
//
// A hidden box never intersects, so the observer watches the nearest rendered
// ancestor: the .shopify-section wrapper, which holds the hidden section's
// place in the flow at zero height. Returns a function that cancels whichever
// start is still pending.
function looxWhenReady(el, run) {
  let done = false;
  let observer = null;
  let idle = null;
  let timer = null;

  const cancel = () => {
    done = true;
    if (observer) observer.disconnect();
    observer = null;
    window.removeEventListener('load', onLoad);
    if (idle !== null && 'cancelIdleCallback' in window) cancelIdleCallback(idle);
    if (timer !== null) clearTimeout(timer);
    idle = timer = null;
  };
  const fire = () => {
    if (done) return;
    cancel();
    run();
  };
  // Safari has no requestIdleCallback; a short timeout after load stands in.
  function onLoad() {
    if ('requestIdleCallback' in window) idle = requestIdleCallback(fire, { timeout: 2000 });
    else timer = setTimeout(fire, 300);
  }

  if (document.readyState === 'complete') onLoad();
  else window.addEventListener('load', onLoad, { once: true });

  if ('IntersectionObserver' in window) {
    // Climb out of every [hidden] ancestor - an attribute walk, no layout read.
    let target = el;
    let hidden;
    while ((hidden = target.closest('[hidden]')) && hidden.parentElement) {
      target = hidden.parentElement;
    }
    observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fire();
      },
      { rootMargin: '1250px 0px' }
    );
    observer.observe(target);
  }

  return cancel;
}

// Input that means the reader has moved on - scrolling, typing or tapping
// elsewhere. Any of it cancels a pending jump to the reviews.
const LOOX_JUMP_CANCEL = ['wheel', 'touchmove', 'keydown', 'pointerdown'];

async function looxFetch(url) {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(url + ' -> ' + res.status);
  return res.json();
}

// Mirrors snippets/rating-stars.liquid so server- and client-rendered stars match.
// labelTemplate carries the localized "%RATING% out of 5 stars" string from the
// section's data-t-stars-label attribute; the English literal is only a fallback.
function looxStars(rating, className, labelTemplate) {
  const value = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));

  const wrap = document.createElement('span');
  wrap.className = className;
  wrap.setAttribute('aria-hidden', 'true');
  wrap.textContent = '★'.repeat(value) + '☆'.repeat(5 - value);

  const label = document.createElement('span');
  label.className = 'visually-hidden';
  label.textContent = (labelTemplate || '%RATING% out of 5 stars').replace('%RATING%', value);

  const frag = document.createDocumentFragment();
  frag.append(wrap, label);
  return frag;
}

// The localized "Name - Verified purchase" author line, shared by both elements.
function looxAuthor(review, verifiedLabel) {
  const name = (review.reviewer && review.reviewer.name) || '';
  return review.verified ? name + ' - ' + (verifiedLabel || 'Verified purchase') : name;
}

function looxDate(value, locale) {
  if (!value) return '';
  const date = new Date(value);
  if (isNaN(date)) return '';
  return date.toLocaleDateString(locale || undefined, { month: 'short', year: 'numeric' });
}

/*
 * <loox-reviews> -- PDP reviews.
 *
 * Markup contract (see sections/product-reviews.liquid):
 *   <loox-reviews data-store-id data-product-id data-locale data-limit hidden>
 *     <span data-loox-summary>   {# "4.6 / 5 - 128" #}
 *     <div  data-loox-bars>      {# .pr-bar-row per star, 5 down to 1 #}
 *     <div  data-loox-toolbar>   {# sort row, hidden below 2 reviews #}
 *     <select data-loox-sort>    {# option values are "<sort>:<direction>" #}
 *     <div  data-loox-list>      {# .pr-card per review #}
 *     <button data-loox-more>    {# pagination, hidden when hasMore is false #}
 *
 * Sorting is a customer-facing control, not a merchandising filter: every review
 * stays in the set whichever order is picked, and "Lowest rated" is offered
 * alongside "Highest rated" so the control cannot read as a filter in disguise.
 * See docs/loox-review-policy-compliance.md.
 */
if (!customElements.get('loox-reviews')) {
  customElements.define(
    'loox-reviews',
    class extends HTMLElement {
      connectedCallback() {
        this.storeId = this.dataset.storeId;
        this.productId = this.dataset.productId;
        if (!this.storeId || !this.productId) return;

        this.locale = this.dataset.locale || undefined;
        this.limit = parseInt(this.dataset.limit, 10) || 10;
        this.tStarsLabel = this.dataset.tStarsLabel;
        this.tVerified = this.dataset.tVerified;
        this.tReply = this.dataset.tReply;
        this.tIncentive = this.dataset.tIncentive;
        this.page = 0;
        this.total = null;

        this.summary = this.querySelector('[data-loox-summary]');
        this.bars = this.querySelector('[data-loox-bars]');
        this.toolbar = this.querySelector('[data-loox-toolbar]');
        this.sortEl = this.querySelector('[data-loox-sort]');
        this.list = this.querySelector('[data-loox-list]');
        this.more = this.querySelector('[data-loox-more]');

        if (this.more) {
          this.more.addEventListener('click', () => {
            // loadPage owns its own errors; the button must never leave an
            // unhandled rejection or stay stuck disabled after a failure.
            this.loadPage().catch((e) => console.warn('[loox-reviews]', e));
          });
        }

        if (this.sortEl) {
          this.sortEl.addEventListener('change', () => {
            this.resort().catch((e) => console.warn('[loox-reviews]', e));
          });
        }

        // A "#reviews" jump - the buy-box rating link, or a shared URL with
        // the hash - targets the section while it is still hidden, so the
        // browser has nowhere to scroll. Load at once and scroll on reveal.
        const section = this.closest('section');
        this.jumpHash = section && section.id ? '#' + section.id : null;
        if (this.jumpHash && !this.onJump) {
          this.onJump = (e) => {
            if (e.type === 'click') {
              if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              const link = e.target.closest && e.target.closest('a[href]');
              if (!link || link.getAttribute('href') !== this.jumpHash) return;
            } else if (location.hash !== this.jumpHash) {
              return;
            }
            this.start(true);
          };
          document.addEventListener('click', this.onJump);
          window.addEventListener('hashchange', this.onJump);
        }

        if (this.jumpHash && location.hash === this.jumpHash) {
          // A reload or back/forward restores the old scroll position - load
          // straight away, but do not override it with a jump.
          const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
          this.start(!nav || nav.type === 'navigate');
        } else {
          this.stopWaiting = looxWhenReady(this, () => this.start(false));
        }
      }

      disconnectedCallback() {
        if (this.stopWaiting) this.stopWaiting();
        this.stopWaiting = null;
        this.disarmJump();
        if (this.onJump) {
          document.removeEventListener('click', this.onJump);
          window.removeEventListener('hashchange', this.onJump);
          this.onJump = null;
        }
      }

      // Idempotent: the idle/near trigger, a jump link and the initial hash
      // can all ask.
      start(jump) {
        if (jump && !this.settled) this.armJump();
        if (this.started) return;
        this.started = true;
        if (this.stopWaiting) this.stopWaiting();
        this.stopWaiting = null;
        this.load();
      }

      // A jump that has to wait for the data lands only if the reader is still
      // where they asked for it. Anyone who scrolls, types or taps in the
      // meantime has moved on, and pulling them back down to the reviews would
      // be a yank. The scroll check catches a scrollbar drag, which raises none
      // of the cancelling events.
      armJump() {
        this.disarmJump();
        this.jumpPending = true;
        this.jumpFromY = window.scrollY;
        this.onJumpCancel = () => this.disarmJump();
        LOOX_JUMP_CANCEL.forEach((type) =>
          window.addEventListener(type, this.onJumpCancel, { capture: true, passive: true })
        );
      }

      disarmJump() {
        this.jumpPending = false;
        if (!this.onJumpCancel) return;
        LOOX_JUMP_CANCEL.forEach((type) =>
          window.removeEventListener(type, this.onJumpCancel, { capture: true })
        );
        this.onJumpCancel = null;
      }

      get base() {
        return looxBase(this.storeId);
      }

      // Option values pack both API params into one control: "date:desc",
      // "rating:desc", "rating:asc". Falling back to date:desc keeps the
      // default deterministic rather than inheriting whatever Loox defaults to,
      // which is what lets the "Most recent" label be accurate.
      get sortParams() {
        const raw = (this.sortEl && this.sortEl.value) || '';
        const [sort, direction] = raw.split(':');
        return { sort: sort || 'date', direction: direction || 'desc' };
      }

      // Re-fetch from page 1 in the newly chosen order. The rendered cards are
      // dropped rather than re-sorted in place: only page 1 is in the DOM, so
      // client-side sorting would order the first 10 reviews and silently
      // mislabel them as the highest rated overall.
      async resort() {
        // loadPage bails out while a request is in flight, so clearing the list
        // first would leave it permanently empty. The select is disabled during
        // a load, which makes this unreachable in practice - it is here so a
        // future caller cannot reintroduce the empty-list failure.
        if (this.loading) return;
        this.page = 0;
        this.list.textContent = '';
        if (this.more) this.more.hidden = true;
        await this.loadPage();
      }

      async load() {
        try {
          // Both calls at once: page 1 does not need the total hint, and the
          // reveal waits on the slower of the two, so chaining them doubled
          // the wait. Page 1 renders into the still-hidden list.
          const [bottomline] = await Promise.all([
            looxFetch(this.base + '/product-reviews/bottomline/' + encodeURIComponent(this.productId)),
            this.loadPage()
          ]);

          // averageRating is 0 (never absent) when a product has no reviews.
          if (!bottomline || !bottomline.totalReviews) return;

          this.renderBottomline(bottomline);
          looxReveal(this);
          // Same landing spot as a native anchor jump (block: start).
          if (this.jumpPending && Math.abs(window.scrollY - this.jumpFromY) <= 1) {
            (this.closest('section') || this).scrollIntoView({ block: 'start' });
          }
        } catch (e) {
          // Reviews are additive - a failed fetch leaves the section hidden
          // rather than breaking the PDP.
          console.warn('[loox-reviews]', e);
        } finally {
          // Shown, empty or failed: either way there is nothing left to jump
          // to later, and a visible section takes the browser's own jump.
          this.settled = true;
          this.disarmJump();
        }
      }

      async loadPage() {
        // Without this guard a double-click fires two requests for the same
        // page (this.page only advances after the await), appending every
        // review twice.
        if (this.loading) return;
        this.loading = true;
        if (this.more) this.more.disabled = true;
        // Disabling the select is what makes the resort race impossible: the
        // order cannot change while a page for the previous order is in flight.
        if (this.sortEl) this.sortEl.disabled = true;
        if (this.list) this.list.setAttribute('aria-busy', 'true');

        try {
          const { sort, direction } = this.sortParams;
          const params = new URLSearchParams({
            product_id: this.productId,
            sort: sort,
            direction: direction,
            page: String(this.page + 1),
            limit: String(this.limit)
          });
          if (this.locale) params.set('locale', this.locale);
          // Passing the known total lets Loox skip a countDocuments query. It
          // survives a resort: reordering the same product's reviews cannot
          // change how many there are.
          if (this.total != null) params.set('total', String(this.total));

          const data = await looxFetch(this.base + '/product-reviews?' + params);
          const reviews = (data && data.reviews) || [];
          const pagination = (data && data.pagination) || {};

          this.page = pagination.page || this.page + 1;
          if (pagination.total != null) this.total = pagination.total;

          reviews.forEach((review) => this.list.appendChild(this.card(review)));

          if (this.more) this.more.hidden = !pagination.hasMore;
        } finally {
          this.loading = false;
          if (this.more) this.more.disabled = false;
          if (this.sortEl) this.sortEl.disabled = false;
          if (this.list) this.list.removeAttribute('aria-busy');
        }
      }

      renderBottomline(bottomline) {
        const average = Number(bottomline.averageRating).toFixed(1);
        if (this.summary) {
          this.summary.textContent = average + ' / 5 - ' + bottomline.totalReviews;
        }
        // Nothing to order with a single review, and the control would imply
        // there is more to see than there is.
        if (this.toolbar && bottomline.totalReviews < 2) this.toolbar.hidden = true;
        if (!this.bars) return;

        const distribution = bottomline.ratingDistribution || {};
        // Keys 1-5 are always present; render highest first to match the design.
        for (let star = 5; star >= 1; star--) {
          const count = Number(distribution[star]) || 0;
          const percent = bottomline.totalReviews ? (count / bottomline.totalReviews) * 100 : 0;

          const row = document.createElement('div');
          row.className = 'pr-bar-row';

          const label = document.createElement('span');
          label.className = 'pr-bar-label';
          label.textContent = String(star);

          const track = document.createElement('div');
          track.className = 'pr-bar-track';
          const fill = document.createElement('div');
          fill.className = 'pr-bar-fill';
          fill.style.width = percent + '%';
          track.appendChild(fill);

          const value = document.createElement('span');
          value.className = 'pr-bar-count';
          value.textContent = String(count);

          row.append(label, track, value);
          this.bars.appendChild(row);
        }
      }

      card(review) {
        const card = document.createElement('div');
        card.className = 'pr-card';

        const head = document.createElement('div');
        head.className = 'pr-card-head';

        const headLeft = document.createElement('div');
        headLeft.appendChild(looxStars(review.rating, 'rating-stars pr-stars', this.tStarsLabel));
        // Loox does not collect a review title, so the card runs
        // stars -> body -> author. There is deliberately no .pr-title here.

        const date = document.createElement('span');
        date.className = 't-xs';
        date.textContent = looxDate(review.date || review.createdAt, this.locale);

        head.append(headLeft, date);
        card.appendChild(head);

        // body is nullable - photo-only reviews carry no text.
        if (review.body) {
          const body = document.createElement('p');
          body.className = 'pr-body';
          body.textContent = review.body;
          card.appendChild(body);
        }

        const media = (review.media || []).filter((item) => item && item.url);
        if (media.length) {
          card.appendChild(this.media(media));

          // Incentive disclosure. Every photo/video reviewer is OFFERED a
          // discount on a future purchase, but not every one of them ends up
          // receiving it, and the API exposes no per-review rewarded flag
          // (checked against the full OpenAPI spec - no such field exists on
          // either the Storefront or the Merchant API). So this states the
          // programme, never the individual: "these reviews earn a discount"
          // is true of all of them, whereas Loox's own badge wording - "the
          // reviewer received a discount" - would be false on some.
          const incentive = document.createElement('p');
          incentive.className = 'pr-incentive';
          incentive.textContent =
            this.tIncentive || 'Photo and video reviews earn a discount on a future purchase.';
          card.appendChild(incentive);
        }

        const author = document.createElement('p');
        author.className = 'pr-author';
        author.textContent = looxAuthor(review, this.tVerified);
        card.appendChild(author);

        // Shop replies. Loox sends reply as {body, repliedAt} on every review
        // and null when unanswered, so the field has to be rendered here - the
        // Loox app block we replaced was the only thing surfacing it before.
        // It sits last, after the author line closes the customer's review.
        if (review.reply && review.reply.body) {
          const reply = document.createElement('div');
          reply.className = 'pr-reply';

          const replyLabel = document.createElement('p');
          replyLabel.className = 'pr-reply-label';
          replyLabel.textContent = this.tReply || 'Reply from ucon acrobatics';

          const replyBody = document.createElement('p');
          replyBody.className = 'pr-reply-body';
          replyBody.textContent = review.reply.body;

          reply.append(replyLabel, replyBody);
          card.appendChild(reply);
        }

        return card;
      }

      media(items) {
        const wrap = document.createElement('div');
        wrap.className = 'pr-media';

        items.forEach((item) => {
          // The schema enum says photo|video but Loox's own example returns
          // "image" - treat anything that is not explicitly video as a photo.
          const isVideo = item.type === 'video';
          const src = isVideo ? item.thumbnailUrl || item.url : item.url;
          if (!src) return;

          const link = document.createElement('a');
          link.className = 'pr-media__item';
          link.href = item.url;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';

          const img = document.createElement('img');
          img.className = 'pr-media__img';
          img.src = src;
          img.loading = 'lazy';
          img.decoding = 'async';
          img.alt = '';

          link.appendChild(img);
          if (isVideo) link.classList.add('pr-media__item--video');
          wrap.appendChild(link);
        });

        return wrap;
      }
    }
  );
}

/*
 * <loox-reviews-strip> -- homepage carousel of the best reviews store-wide.
 *
 * Markup contract (see sections/homepage-reviews.liquid):
 *   <loox-reviews-strip data-store-id data-locale data-limit hidden>
 *     <span data-loox-rating>  {# "4.7" #}
 *     <span data-loox-stars>   {# aggregate stars #}
 *     <span data-loox-count>   {# "from 128 reviews" #}
 *     <div  data-loox-list>    {# .hp-reviews__card per review #}
 *
 * Uses sort=date&direction=desc. See the note in load() for why not
 * sort=featured. This shelf has no sort control: it is a fixed marketing strip,
 * not a browsable list - the browsable one is <loox-reviews> on the PDP.
 */
if (!customElements.get('loox-reviews-strip')) {
  customElements.define(
    'loox-reviews-strip',
    class extends HTMLElement {
      connectedCallback() {
        this.storeId = this.dataset.storeId;
        if (!this.storeId) return;

        this.locale = this.dataset.locale || undefined;
        this.limit = parseInt(this.dataset.limit, 10) || 4;
        // Comma-separated star ratings to show, e.g. "5" or "4,5". Blank = all.
        this.minRating = this.dataset.minRating || '';
        this.tStarsLabel = this.dataset.tStarsLabel;
        this.tVerified = this.dataset.tVerified;
        this.tCountOne = this.dataset.tCountOne;
        this.tCountOther = this.dataset.tCountOther;

        this.ratingEl = this.querySelector('[data-loox-rating]');
        this.starsEl = this.querySelector('[data-loox-stars]');
        this.countEl = this.querySelector('[data-loox-count]');
        this.list = this.querySelector('[data-loox-list]');

        if (this.stopWaiting) this.stopWaiting();
        this.stopWaiting = looxWhenReady(this, () => {
          this.stopWaiting = null;
          this.load();
        });
      }

      disconnectedCallback() {
        if (this.stopWaiting) this.stopWaiting();
        this.stopWaiting = null;
      }

      async load() {
        try {
          // NOT sort=featured. Loox documents it as "pinned first, then your
          // best reviews", but against the real catalogue it returns
          // 1,5,5,5,2,5,5,5,1,5 - a 1-star review leads. Recent-and-positive is
          // what a homepage shelf wants; the honest aggregate sits above it.
          // No `total` hint here. It exists to skip a count query across pages,
          // but this shelf only ever fetches page 1, and the bottomline total is
          // the UNFILTERED count - passing it alongside a rating filter would
          // hand Loox a number that does not match the query.
          const params = new URLSearchParams({
            sort: 'date',
            direction: 'desc',
            page: '1',
            limit: String(this.limit)
          });
          if (this.minRating) params.set('rating', this.minRating);
          if (this.locale) params.set('locale', this.locale);

          // Both calls at once - the list does not depend on the aggregate, and
          // the reveal waits on the slower of the two. No productId on the
          // bottomline path = the store-wide aggregate.
          const [bottomline, data] = await Promise.all([
            looxFetch(looxBase(this.storeId) + '/product-reviews/bottomline'),
            looxFetch(looxBase(this.storeId) + '/product-reviews?' + params)
          ]);
          if (!bottomline || !bottomline.totalReviews) return;
          const reviews = (data && data.reviews) || [];
          if (!reviews.length) return;

          this.renderAggregate(bottomline);
          reviews.forEach((review) => this.list.appendChild(this.card(review)));
          looxReveal(this);

          // <scroll-rail> and <slider-nav> both measure on connect and observe
          // the track with a ResizeObserver. Appending children does not change
          // the track's own box, so neither observer fires - but both also
          // listen for scroll on the track, so one synthetic event re-measures.
          const track = this.list;
          if (track) track.dispatchEvent(new Event('scroll'));
        } catch (e) {
          console.warn('[loox-reviews-strip]', e);
        }
      }

      renderAggregate(bottomline) {
        const average = Number(bottomline.averageRating);
        if (this.ratingEl) this.ratingEl.textContent = average.toFixed(1);
        if (this.countEl) {
          const template = bottomline.totalReviews === 1
            ? this.tCountOne || 'from %COUNT% review'
            : this.tCountOther || 'from %COUNT% reviews';
          this.countEl.textContent = template.replace('%COUNT%', bottomline.totalReviews);
        }
        if (this.starsEl) {
          this.starsEl.textContent = '';
          this.starsEl.appendChild(looxStars(average, 'hp-reviews__stars', this.tStarsLabel));
        }
      }

      card(review) {
        const card = document.createElement('article');
        card.className = 'hp-reviews__card';

        const head = document.createElement('div');
        head.className = 'hp-reviews__card-head';
        head.appendChild(looxStars(review.rating, 'hp-reviews__card-stars', this.tStarsLabel));

        const date = document.createElement('span');
        date.className = 't-xs';
        date.textContent = looxDate(review.date || review.createdAt, this.locale);
        head.appendChild(date);
        card.appendChild(head);

        // Loox has no review title. On a store-wide shelf the product being
        // reviewed is the more useful line anyway, so it takes the title slot.
        const product = review.product;
        if (product && product.name) {
          const title = document.createElement('p');
          title.className = 'hp-reviews__card-title';
          if (product.url) {
            const link = document.createElement('a');
            link.className = 'link-subtle';
            link.href = product.url;
            link.textContent = product.name;
            title.appendChild(link);
          } else {
            title.textContent = product.name;
          }
          card.appendChild(title);
        }

        if (review.body) {
          const body = document.createElement('p');
          body.className = 'hp-reviews__card-body';
          body.textContent = review.body;
          card.appendChild(body);
        }

        const author = document.createElement('p');
        author.className = 'hp-reviews__card-author';
        author.textContent = looxAuthor(review, this.tVerified);
        card.appendChild(author);

        return card;
      }
    }
  );
}
